import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { lazy, Suspense, useEffect, useCallback, type ReactNode } from 'react';
import { AppLayout } from './layouts/AppLayout';
import { Dashboard } from './pages/Dashboard';
import { Campaigns } from './pages/CampaignsReal';
import { CreateAdWizard } from './pages/CreateAdWizard';
import Autopilot from './pages/Autopilot';
import Analytics from './pages/Analytics';
import MarketInsights from './pages/MarketInsights';
import KnowledgeBank from './pages/KnowledgeBank';
import AuthAccounts from './pages/AuthAccounts';
import PlatformAccounts from './pages/PlatformAccounts';
import Budgets from './pages/Budgets';
import LocalAgentReleaseManagement from './pages/LocalAgentReleaseManagement';
import OAuthCallback from './pages/OAuthCallback';
import SSOCallback from './pages/SSOCallback';
import { generateCodeVerifier, generateCodeChallenge, generateState } from './lib/pkce';
import FacebookPlanEditPage from './pages/FacebookPlanEditPage';
import GooglePlanEditPage from './pages/GooglePlanEditPage';
import TikTokPlanEditPage from './pages/TikTokPlanEditPage';
import { useAppStore } from './store/useAppStore';
import Forbidden from './pages/Forbidden';
import TtInsights from './pages/TtInsights';
import FbInsights from './pages/FbInsights';
import GgInsights from './pages/GgInsights';
import ChannelMonitoring from './pages/ChannelMonitoring';
import BingDeliveryDashboard from './pages/BingDeliveryDashboard';
import CriteoDeliveryDashboard from './pages/CriteoDeliveryDashboard';
import GoogleDeliveryDashboard from './pages/GoogleDeliveryDashboard';
import TiktokDeliveryDashboard from './pages/TiktokDeliveryDashboard';
import SnapchatDeliveryDashboard from './pages/SnapchatDeliveryDashboard';
import MetaDeliveryDashboard from './pages/MetaDeliveryDashboard';
import ApplovinDeliveryDashboard from './pages/ApplovinDeliveryDashboard';
import RtbDeliveryDashboard from './pages/RtbDeliveryDashboard';
import ContentMonitoring from './pages/ContentMonitoring';
import TargetTracking from './pages/TargetTracking';
import SkuRoi from './pages/SkuRoi';
import ChannelWideTable from './pages/ChannelWideTable';
import AcquisitionCostDashboard from './pages/AcquisitionCostDashboard';
import SiteTrafficMonitoring from './pages/SiteTrafficMonitoring';
import SiteBounceRate from './pages/SiteBounceRate';
import SiteChannelAcquisition from './pages/SiteChannelAcquisition';
import SiteChannelPortal from './pages/SiteChannelPortal';
import SiteCostTypeMonitoring from './pages/SiteCostTypeMonitoring';
import SiteTargetTypeLayered from './pages/SiteTargetTypeLayered';
import SiteDeliveryTarget from './pages/SiteDeliveryTarget';
import { RequirePagePermission } from './components/RequirePagePermission';
import { RequireChannelAccess } from './components/RequireChannelAccess';
import { getFirstAllowedChannel } from './lib/adPermissions';


const GATEKEEPER_URL = (import.meta.env.VITE_GATEKEEPER_URL || 'https://test-gatekeeper.xmslol.cn').replace(/\/+$/, '')
const OAUTH_CLIENT_ID = import.meta.env.VITE_OAUTH_CLIENT_ID || 'ad-platform'
const OAUTH_REDIRECT_URI = import.meta.env.VITE_OAUTH_REDIRECT_URI || `${typeof window !== 'undefined' ? window.location.origin : ''}/sso/callback`

const SiteUserPortal = lazy(() => import('./pages/SiteUserPortal'));
const Notifications = lazy(() => import('./pages/Notifications'));
const NotificationManagement = lazy(() => import('./pages/NotificationManagement'));
const UsageAnalytics = lazy(() => import('./pages/UsageAnalytics'));

function LazyPage({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>加载中...</div>}>
      {children}
    </Suspense>
  );
}

// 登出蒙层：覆盖在当前页面上，不跳页
function SessionOverlay() {
  const handleLogin = useCallback(() => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('user_info')
    void (async () => {
      const verifier = generateCodeVerifier()
      const challenge = await generateCodeChallenge(verifier)
      const state = generateState()
      localStorage.setItem('oauth_code_verifier', verifier)
      localStorage.setItem('oauth_state', state)
      localStorage.setItem('oauth_redirect_after', window.location.pathname + window.location.search)
      const authorizeUrl = new URL(`${GATEKEEPER_URL}/api/oauth/authorize`)
      authorizeUrl.searchParams.set('client_id', OAUTH_CLIENT_ID)
      authorizeUrl.searchParams.set('redirect_uri', OAUTH_REDIRECT_URI)
      authorizeUrl.searchParams.set('state', state)
      authorizeUrl.searchParams.set('code_challenge', challenge)
      authorizeUrl.searchParams.set('code_challenge_method', 'S256')
      // 不清 gatekeeper session，直接走 authorize，session 有效则无感续期
      // replace 避免 authorize → callback 的跳转被记入历史栈，防止手机返回键陷入循环
      window.location.replace(authorizeUrl.toString())
    })()
  }, [])

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,15,35,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '40px 48px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        minWidth: 320,
      }}>
        <img src="/icons/logo.svg" alt="鹿鸣" width="44" height="44" />
        <p style={{ margin: 0, fontSize: 12, color: '#6366f1', fontWeight: 500 }}>鹿鸣智能广告全域增长平台</p>
        <p style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#1a2233' }}>已退出</p>
        <p style={{ margin: 0, fontSize: 13, color: '#9aa5b4', textAlign: 'center' }}>
          请重新登录以继续使用
        </p>
        <button
          onClick={handleLogin}
          style={{
            marginTop: 4, width: '100%', height: 44, borderRadius: 10,
            background: 'linear-gradient(135deg,#1210B8,#2D24D6)',
            color: '#fff', border: 'none', fontSize: 15, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.04em',
          }}
        >
          重新登录
        </button>
      </div>
    </div>
  )
}

// cookie 存在表示用户在 90 天记忆期内，token 过期时应自动续期而非显示蒙层
function hasRememberCookie() {
  return document.cookie.split(';').some(c => c.trim().startsWith('session_remember='))
}

// Auth guard：无 token 时跳转到 gatekeeper SSO 授权；登出状态原地显示蒙层
function RequireAuth() {
  const token = localStorage.getItem('access_token');
  const isLoggedOut = useAppStore(s => s.isLoggedOut)
  const setLoggedOut = useAppStore(s => s.setLoggedOut)

  // token 恢复（重新登录成功）后，清除登出状态
  useEffect(() => {
    if (token && isLoggedOut) setLoggedOut(false)
  }, [token, isLoggedOut, setLoggedOut])

  // 自动走 SSO：无 token 时；或 token 过期（isLoggedOut）但仍在 90 天记忆期内
  useEffect(() => {
    if (token) return;
    // 超出记忆期且主动登出 → 显示蒙层，不自动跳
    if (isLoggedOut && !hasRememberCookie()) return;
    let cancelled = false;
    void (async () => {
      const verifier = generateCodeVerifier()
      const challenge = await generateCodeChallenge(verifier)
      const state = generateState()
      if (cancelled) return;
      localStorage.setItem('oauth_code_verifier', verifier)
      localStorage.setItem('oauth_state', state)
      localStorage.setItem('oauth_redirect_after', window.location.pathname + window.location.search)
      const authorizeUrl = new URL(`${GATEKEEPER_URL}/api/oauth/authorize`)
      authorizeUrl.searchParams.set('client_id', OAUTH_CLIENT_ID)
      authorizeUrl.searchParams.set('redirect_uri', OAUTH_REDIRECT_URI)
      authorizeUrl.searchParams.set('state', state)
      authorizeUrl.searchParams.set('code_challenge', challenge)
      authorizeUrl.searchParams.set('code_challenge_method', 'S256')
      window.location.replace(authorizeUrl.toString())
    })()
    return () => { cancelled = true; }
  }, [token, isLoggedOut]) // eslint-disable-line react-hooks/exhaustive-deps

  // 超出记忆期 + token 已失效：显示蒙层等待手动重登
  if (!token && isLoggedOut && !hasRememberCookie()) {
    return (
      <>
        <Outlet />
        <SessionOverlay />
      </>
    )
  }

  if (!token) return null;
  return <Outlet />;
}

function CampaignChannelRedirect({ suffix = '' }: { suffix?: '' | '/create' }) {
  const permissionsCheckDone = useAppStore((s) => s.permissionsCheckDone)
  const permissionsLoaded = useAppStore((s) => s.permissionsLoaded)
  const sitesLoaded = useAppStore((s) => s.sitesLoaded)
  const adContext = useAppStore((s) => s.adContext)

  if (!permissionsCheckDone || !permissionsLoaded || !sitesLoaded) return null
  return <Navigate to={`/campaigns/${getFirstAllowedChannel(adContext?.channel_access)}${suffix}`} replace />
}


export const router = createBrowserRouter([
  {
    path: '/sso/callback',
    element: <SSOCallback />,
  },
  {
    path: '/adPLaunch',
    element: <Navigate to="/adPLaunch.html" replace />,
  },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      // 暂时隐藏首页和安装包下载；在布局外跳转，避免生成多余工作区标签。
      { index: true, element: <Navigate to="/notifications" replace /> },
      { path: 'local-agent', element: <Navigate to="/notifications" replace /> },
      { path: 'local-agent/downloads', element: <Navigate to="/notifications" replace /> },
      {
        path: 'oauth/callback',
        element: <OAuthCallback />,
      },
      {
        path: 'forbidden',
        element: <Forbidden />,
      },
      {
        element: <AppLayout />,
        children: [
          { path: 'local-agent/releases', element: <RequirePagePermission code="ad:role:admin"><LocalAgentReleaseManagement /></RequirePagePermission> },
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'campaigns', element: <CampaignChannelRedirect /> },
          { path: 'campaigns/create', element: <CampaignChannelRedirect suffix="/create" /> },
          { path: 'campaigns/:platform', element: <RequireChannelAccess fallbackBasePath="/campaigns"><Campaigns /></RequireChannelAccess> },
          { path: 'campaigns/:platform/create', element: <RequireChannelAccess fallbackBasePath="/campaigns"><CreateAdWizard /></RequireChannelAccess> },
          { path: 'campaigns/edit/facebook/:entity/:id', element: <RequireChannelAccess channel="facebook" fallbackBasePath="/campaigns"><FacebookPlanEditPage /></RequireChannelAccess> },
          { path: 'campaigns/edit/google/:entity/:id', element: <RequireChannelAccess channel="google" fallbackBasePath="/campaigns"><GooglePlanEditPage /></RequireChannelAccess> },
          { path: 'campaigns/edit/tiktok/:entity/:id', element: <RequireChannelAccess channel="tiktok" fallbackBasePath="/campaigns"><TikTokPlanEditPage /></RequireChannelAccess> },
          { path: 'autopilot', element: <Autopilot /> },
          { path: 'budgets', element: <Budgets /> },
          { path: 'insights', element: <MarketInsights /> },
          { path: 'analytics', element: <Analytics /> },
          { path: 'knowledge', element: <KnowledgeBank /> },
          { path: 'ad-analysis/tt-insights', element: <RequirePagePermission code="ad-mgmt:ad-analysis:tt"><RequireChannelAccess channel="tiktok"><TtInsights /></RequireChannelAccess></RequirePagePermission> },
          { path: 'ad-analysis/fb-insights', element: <RequirePagePermission code="ad-mgmt:ad-analysis:fb"><RequireChannelAccess channel="facebook"><FbInsights /></RequireChannelAccess></RequirePagePermission> },
          { path: 'ad-analysis/gg-insights', element: <RequirePagePermission code="ad-mgmt:ad-analysis:gg"><RequireChannelAccess channel="google"><GgInsights /></RequireChannelAccess></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/bing', element: <RequirePagePermission code="stats:delivery:bing"><BingDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/criteo', element: <RequirePagePermission code="stats:delivery:criteo"><CriteoDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/google', element: <RequirePagePermission code="stats:delivery:google"><GoogleDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/tiktok', element: <RequirePagePermission code="stats:delivery:tiktok"><TiktokDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/snapchat', element: <RequirePagePermission code="stats:delivery:snapchat"><SnapchatDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/meta', element: <RequirePagePermission code="stats:delivery:meta"><MetaDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/applovin', element: <RequirePagePermission code="stats:delivery:applovin"><ApplovinDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/ad-dashboard/rtb', element: <RequirePagePermission code="stats:delivery:rtb"><RtbDeliveryDashboard /></RequirePagePermission> },
          { path: 'ad-analysis/channel-monitoring', element: <RequirePagePermission code="ad-mgmt:ad-analysis:monitoring"><ChannelMonitoring /></RequirePagePermission> },
          { path: 'ad-analysis/target-tracking', element: <TargetTracking /> },
          { path: 'ad-analysis/sku-roi', element: <RequirePagePermission code="stats:sku-roi"><SkuRoi /></RequirePagePermission> },
          { path: 'ad-analysis/channel-wide-table', element: <RequirePagePermission code="stats:channel-wide-table"><ChannelWideTable /></RequirePagePermission> },
          { path: 'ad-analysis/acquisition-cost', element: <RequirePagePermission code="stats:acquisition-cost"><AcquisitionCostDashboard /></RequirePagePermission> },
          { path: 'site-data/traffic-monitoring', element: <RequirePagePermission code="stats:traffic"><SiteTrafficMonitoring /></RequirePagePermission> },
          { path: 'site-data/bounce-rate', element: <RequirePagePermission code="stats:bounce"><SiteBounceRate /></RequirePagePermission> },
          { path: 'site-data/channel-acquisition', element: <RequirePagePermission code="stats:acquisition"><SiteChannelAcquisition /></RequirePagePermission> },
          { path: 'site-data/user-portal', element: <RequirePagePermission code="stats:user-portal"><LazyPage><SiteUserPortal /></LazyPage></RequirePagePermission> },
          { path: 'site-data/channel-portal', element: <RequirePagePermission code="stats:portal"><SiteChannelPortal /></RequirePagePermission> },
          { path: 'site-data/cost-type-monitoring', element: <RequirePagePermission code="stats:cost-type"><SiteCostTypeMonitoring /></RequirePagePermission> },
          { path: 'site-data/target-type-layered', element: <RequirePagePermission code="stats:target-type"><SiteTargetTypeLayered /></RequirePagePermission> },
          { path: 'site-data/delivery-target', element: <RequirePagePermission code="stats:delivery-target"><SiteDeliveryTarget /></RequirePagePermission> },
          { path: 'ad-analysis/content-monitoring', element: <ContentMonitoring /> },

          { path: 'auth-accounts', element: <AuthAccounts /> },
          { path: 'platform-accounts', element: <PlatformAccounts /> },
          { path: 'notifications', element: <LazyPage><Notifications /></LazyPage> },
          { path: 'system/notifications', element: <RequirePagePermission code="ad:role:admin"><LazyPage><NotificationManagement /></LazyPage></RequirePagePermission> },
          { path: 'usage-analytics', element: <RequirePagePermission code="ad:role:admin"><LazyPage><UsageAnalytics /></LazyPage></RequirePagePermission> },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);
